import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  IconButton,
  TextField,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  useTheme,
  useMediaQuery,
  MenuItem,
  Alert,
} from '@mui/material';
import {
  PlayArrow as PlayArrowIcon,
  Stop as StopIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  YouTube as YouTubeIcon,
  Help as HelpIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import PageLayout from './PageLayout';
import { useAuth } from '../contexts/AuthContext';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../services/firebase';
import { usePlayer } from '../contexts/PlayerContext';
import { getContrastText } from '../utils/colorUtils';
import { usePlan } from '../contexts/PlanContext';
import PlanComparisonDialog from './shared/PlanComparisonDialog';
import { hasReachedLimit } from '../config/planLimits';

export default function Player({ customColors }) {
  const { currentUser } = useAuth();
  const { handlePlayVideo, currentTrack } = usePlayer();
  const { userPlan } = usePlan();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [videos, setVideos] = useState([]);
  const [openVideoDialog, setOpenVideoDialog] = useState(false);
  const [openEditVideoDialog, setOpenEditVideoDialog] = useState(false);
  const [editingVideo, setEditingVideo] = useState(null);
  const [videoCategories, setVideoCategories] = useState([
    'Motivacional',
    'Devocional',
    'Lo-fi',
    'Lounge',
    'Sons da Natureza',
    'Música Clássica',
    'Meditação',
    'Estudo',
    'Ambiente'
  ]);
  const [newCategory, setNewCategory] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [newVideoUrl, setNewVideoUrl] = useState('');
  const [newVideoTitle, setNewVideoTitle] = useState('');
  const [videoNote, setVideoNote] = useState('');
  const [isLoadingVideoInfo, setIsLoadingVideoInfo] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState({ open: false, videoId: null });
  const [openVideoHelp, setOpenVideoHelp] = useState(false);
  const [newVideoCategory, setNewVideoCategory] = useState('');
  const [showPlanDialog, setShowPlanDialog] = useState(false);

  // Limitações do plano básico
  const BASIC_PLAN_LIMITS = {
    maxVideos: 2,
  };

  // Função para verificar se atingiu o limite
  const hasReachedPlayerLimit = (type) => {
    if (userPlan === 'PRO') return false;
    
    if (type === 'videos') {
      return hasReachedLimit(userPlan, 'maxVideos', videos.length);
    }
    return false;
  };

  useEffect(() => {
    loadUserData();
    // Carregar a API do YouTube
    const tag = document.createElement('script');
    tag.src = 'https://www.youtube.com/iframe_api';
    const firstScriptTag = document.getElementsByTagName('script')[0];
    if (firstScriptTag && firstScriptTag.parentNode) {
      firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
    }

    // Adicionar event listener para visibilidade da página
    const handleVisibilityChange = () => {
      if (!currentUser) {
        setVideos([]);
        setOpenVideoDialog(false);
        setNewVideoUrl('');
        setNewVideoTitle('');
        setVideoNote('');
        setIsLoadingVideoInfo(false);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup function
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [currentUser]);

  const loadUserData = async () => {
    if (!currentUser) return;
    try {
      const userRef = doc(db, 'users', currentUser.uid);
      const userDoc = await getDoc(userRef);
      if (userDoc.exists()) {
        const userData = userDoc.data();
        if (userData.youtubeVideos) {
          setVideos(userData.youtubeVideos);
        }
      }
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    }
  };

  const saveUserData = async (newVideos) => {
    if (!currentUser) return;
    try {
      const userRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userRef, {
        youtubeVideos: newVideos || videos
      });
    } catch (error) {
      console.error('Erro ao salvar dados:', error);
    }
  };

  const fetchVideoInfo = async (videoId) => {
    try {
      setIsLoadingVideoInfo(true);
      const oembedResponse = await fetch(`https://www.youtube.com/oembed?url=https://www.youtube.com/watch?v=${videoId}&format=json`);
      const oembedData = await oembedResponse.json();
      if (oembedData.title) {
        setNewVideoTitle(oembedData.title);
      }
    } catch (error) {
      console.error('Erro ao buscar informações do vídeo:', error);
    } finally {
      setIsLoadingVideoInfo(false);
    }
  };

  const handleAddVideo = () => {
    if (!newVideoUrl) return;
    
    if (hasReachedPlayerLimit('videos')) {
      setShowPlanDialog(true);
      setOpenVideoDialog(false);
      return;
    }

    // Extract video ID from URL
    const videoId = newVideoUrl.match(/(?:youtu\.be\/|youtube\.com(?:\/embed\/|\/v\/|\/watch\?v=|\/watch\?.+&v=))([\w-]{11})/);
    
    if (videoId) {
      const newVideo = {
        id: Date.now().toString(),
        title: newVideoTitle,
        videoId: videoId[1],
        url: newVideoUrl,
        note: videoNote,
        category: newVideoCategory || videoCategories[0]
      };

      const updatedVideos = [...videos, newVideo];
      setVideos(updatedVideos);
      saveUserData(updatedVideos);
      handleCloseVideoDialog();
    }
  };

  const handleCloseVideoDialog = () => {
    setNewVideoUrl('');
    setNewVideoTitle('');
    setVideoNote('');
    setNewVideoCategory('');
    setOpenVideoDialog(false);
  };

  const handleEditVideo = () => {
    if (!editingVideo) return;

    const updatedVideos = videos.map(video => 
      video.id === editingVideo.id 
        ? { ...video, note: editingVideo.note, category: editingVideo.category }
        : video
    );

    setVideos(updatedVideos);
    saveUserData(updatedVideos);
    setEditingVideo(null);
    setOpenEditVideoDialog(false);
  };

  const handleAddCategory = () => {
    if (!newCategory.trim()) return;
    setVideoCategories(prev => [...prev, newCategory.trim()]);
    setNewCategory('');
  };

  const filteredVideos = selectedCategory === 'all' 
    ? videos 
    : videos.filter(video => video.category === selectedCategory);

  const handleDeleteVideo = (videoId) => {
    const updatedVideos = videos.filter(video => video.id !== videoId);
    setVideos(updatedVideos);
    saveUserData(updatedVideos);
    setDeleteConfirmation({ open: false, videoId: null });
  };

  const openDeleteConfirmation = (videoId) => {
    setDeleteConfirmation({ open: true, videoId });
  };

  const handlePlayYouTubeVideo = (video) => {
    handlePlayVideo(video);
  };

  const handleRemoveAllVideos = () => {
    if (window.confirm('Tem certeza que deseja remover todos os vídeos da sua lista?')) {
      setVideos([]);
      saveUserData([]);
    }
  };

  return (
    <PageLayout
      title="Player"
      customColors={customColors}
      actionButtons={
        <Button
          variant="outlined"
          color="error"
          startIcon={<DeleteIcon />}
          onClick={handleRemoveAllVideos}
        >
          Remover Todos os Vídeos
        </Button>
      }
    >
      <Box sx={{ mb: 4 }}>
        <Grid container spacing={3} alignItems="flex-start">
          {/* YouTube Videos Section */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                {userPlan === 'BASIC' && (
                  <Alert 
                    severity="info" 
                    sx={{ mb: 2 }}
                    action={
                      <Button 
                        color="inherit" 
                        size="small" 
                        onClick={() => setShowPlanDialog(true)}
                      >
                        FAZER UPGRADE
                      </Button>
                    }
                  >
                    Plano Básico: {videos.length}/{BASIC_PLAN_LIMITS.maxVideos} vídeos disponíveis
                  </Alert>
                )}
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  mb: 2 
                }}>
                  <Typography variant="h6" component="div">
                    Youtube
                  </Typography>
                  <IconButton
                    onClick={() => setOpenVideoHelp(true)}
                    color="primary"
                    size="small"
                  >
                    <HelpIcon />
                  </IconButton>
                </Box>
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column',
                  mb: 2 
                }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    Adicione links de vídeos ou playlists do YouTube para reproduzir o áudio dentro da Habitop e manter o foco durante suas sessões de trabalho. O áudio será reproduzido em segundo plano enquanto você navega pelas sessões.
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontStyle: 'italic' }}>
                    Dica: Você pode adicionar vídeos de sons ambientes, lo-fi, música para estudar, etc.
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setOpenVideoDialog(true)}
                    sx={{
                      bgcolor: customColors?.primary || 'primary.main',
                      color: customColors?.primary ? getContrastText(customColors.primary) : 'white',
                      '&:hover': {
                        bgcolor: customColors?.primary || 'primary.main',
                        opacity: 0.9
                      },
                      alignSelf: 'flex-start',
                      mt: 2,
                      mb: 2
                    }}
                  >
                    Adicionar Vídeo
                  </Button>
                </Box>

                {/* Filter Section */}
                <Box sx={{ mb: 2 }}>
                  <TextField
                    select
                    fullWidth
                    label="Filtrar por categoria"
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    size="small"
                  >
                    <MenuItem value="all">Todas as categorias</MenuItem>
                    {videoCategories.map((category) => (
                      <MenuItem key={category} value={category}>
                        {category}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>

                {/* Video List */}
                <Grid container spacing={2}>
                  {filteredVideos.map((video) => (
                    <Grid item xs={12} sm={6} key={video.id}>
                      <Card 
                        variant="outlined"
                        sx={{
                          bgcolor: currentTrack?.id === video.id && currentTrack?.videoId ? 'action.selected' : 'inherit'
                        }}
                      >
                        <CardContent sx={{ 
                          display: 'flex',
                          flexDirection: { xs: 'column', sm: 'row' },
                          justifyContent: 'space-between',
                          alignItems: { xs: 'flex-start', sm: 'center' },
                          py: 1,
                          '&:last-child': { pb: 1 }
                        }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                            <YouTubeIcon color="error" />
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="body2" noWrap sx={{ maxWidth: { xs: '100%', sm: 500 } }}>
                                {video.title}
                              </Typography>
                              {video.category && (
                                <Typography 
                                  variant="caption"
                                  sx={{ 
                                    display: 'inline-block',
                                    bgcolor: 'action.selected',
                                    px: 1,
                                    py: 0.25,
                                    borderRadius: 1,
                                    mb: 0.5
                                  }}
                                >
                                  {video.category}
                                </Typography>
                              )}
                              {video.note && (
                                <Typography 
                                  variant="caption" 
                                  color="text.secondary" 
                                  sx={{ 
                                    display: 'block',
                                    fontStyle: 'italic',
                                    maxWidth: { xs: '100%', sm: 500 },
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                  }}
                                >
                                  {video.note}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                          <Box sx={{ 
                            display: 'flex',
                            justifyContent: { xs: 'center', sm: 'flex-end' },
                            width: '100%',
                            mt: { xs: 1, sm: 0 },
                            borderTop: { xs: `1px solid ${theme.palette.divider}`, sm: 'none' },
                            pt: { xs: 1, sm: 0 },
                            gap: { xs: 2, sm: 0 }
                          }}>
                            <IconButton
                              size="small"
                              onClick={() => {
                                setEditingVideo(video);
                                setOpenEditVideoDialog(true);
                              }}
                              color="primary"
                            >
                              <EditIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handlePlayYouTubeVideo(video)}
                              sx={{ 
                                color: currentTrack?.id === video.id && currentTrack?.videoId ? 'error.main' : (customColors?.primary || 'primary.main')
                              }}
                            >
                              {currentTrack?.id === video.id && currentTrack?.videoId ? <StopIcon /> : <PlayArrowIcon />}
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => openDeleteConfirmation(video.id)}
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Delete Video Confirmation Dialog */}
      <Dialog
        open={deleteConfirmation.open}
        onClose={() => setDeleteConfirmation({ open: false, videoId: null })}
      >
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja excluir este vídeo da sua lista?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDeleteConfirmation({ open: false, videoId: null })}
          >
            Cancelar
          </Button>
          <Button 
            onClick={() => handleDeleteVideo(deleteConfirmation.videoId)}
            color="error"
            variant="contained"
          >
            Excluir
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Video Dialog */}
      <Dialog open={openVideoDialog} onClose={handleCloseVideoDialog}>
        <DialogTitle>Adicionar Vídeo do YouTube</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 2, minWidth: 300 }}>
            <TextField
              label="URL do YouTube"
              value={newVideoUrl}
              onChange={(e) => {
                setNewVideoUrl(e.target.value);
                const videoId = e.target.value.match(/(?:youtu\.be\/|youtube\.com(?:\/embed\/|\/v\/|\/watch\?v=|\/watch\?.+&v=))([\w-]{11})/);
                if (videoId) {
                  fetchVideoInfo(videoId[1]);
                }
              }}
              fullWidth
              helperText="Ex: https://youtube.com/watch?v=..."
            />
            <TextField
              label="Título do Vídeo"
              value={newVideoTitle}
              disabled
              fullWidth
              InputProps={{
                readOnly: true,
              }}
              helperText="O título será obtido automaticamente do YouTube"
            />
            <TextField
              select
              label="Categoria"
              value={newVideoCategory || videoCategories[0]}
              onChange={(e) => setNewVideoCategory(e.target.value)}
              fullWidth
            >
              {videoCategories.map((category) => (
                <MenuItem key={category} value={category}>
                  {category}
                </MenuItem>
              ))}
            </TextField>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                label="Nova categoria"
                value={newCategory}
                onChange={(e) => setNewCategory(e.target.value)}
                size="small"
                fullWidth
              />
              <Button
                variant="outlined"
                onClick={handleAddCategory}
                sx={{ whiteSpace: 'nowrap' }}
              >
                Adicionar
              </Button>
            </Box>
            <TextField
              label="Anotações (opcional)"
              value={videoNote}
              onChange={(e) => setVideoNote(e.target.value)}
              fullWidth
              multiline
              rows={3}
              placeholder="Adicione suas anotações sobre este vídeo..."
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseVideoDialog}>Cancelar</Button>
          <Button 
            onClick={handleAddVideo}
            variant="contained"
            sx={{
              bgcolor: customColors?.primary || 'primary.main',
              color: customColors?.primary ? getContrastText(customColors.primary) : 'white',
              '&:hover': {
                bgcolor: customColors?.primary || 'primary.main',
                opacity: 0.9
              }
            }}
          >
            Adicionar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Help Dialog */}
      <Dialog open={openVideoHelp} onClose={() => setOpenVideoHelp(false)}>
        <DialogTitle>Como usar os Vídeos para Foco?</DialogTitle>
        <DialogContent>
          <Typography paragraph>
            Os vídeos para foco são uma ferramenta poderosa para melhorar sua concentração durante as sessões de trabalho:
          </Typography>
          <Typography component="div" sx={{ mb: 2 }}>
            <ul>
              <li>Adicione vídeos do YouTube com sons ambientes ou músicas relaxantes</li>
              <li>O áudio será reproduzido em segundo plano enquanto você usa a Habitop</li>
              <li>Você pode alternar entre diferentes vídeos conforme sua preferência</li>
              <li>Organize seus vídeos favoritos com notas personalizadas</li>
            </ul>
          </Typography>
          <Typography paragraph>
            Sugestões de conteúdo:
          </Typography>
          <Typography component="div">
            <ul>
              <li>Música lo-fi</li>
              <li>Sons da natureza</li>
              <li>Ruído branco</li>
              <li>Música clássica</li>
              <li>Sons de café/biblioteca</li>
              <li>Discursos motivacionais</li>
            </ul>
          </Typography>
          <Typography sx={{ mt: 2 }}>
            Experimente diferentes tipos de sons para descobrir o que melhor ajuda em sua concentração.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenVideoHelp(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Video Dialog */}
      <Dialog open={openEditVideoDialog} onClose={() => setOpenEditVideoDialog(false)}>
        <DialogTitle>Editar Vídeo</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 2, minWidth: 300 }}>
            <Typography variant="subtitle1">
              {editingVideo?.title}
            </Typography>
            <TextField
              select
              label="Categoria"
              value={editingVideo?.category || ''}
              onChange={(e) => setEditingVideo(prev => ({ ...prev, category: e.target.value }))}
              fullWidth
            >
              {videoCategories.map((category) => (
                <MenuItem key={category} value={category}>
                  {category}
                </MenuItem>
              ))}
            </TextField>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                label="Nova categoria"
                value={newCategory}
                onChange={(e) => setNewCategory(e.target.value)}
                size="small"
                fullWidth
              />
              <Button
                variant="outlined"
                onClick={handleAddCategory}
                sx={{ whiteSpace: 'nowrap' }}
              >
                Adicionar
              </Button>
            </Box>
            <TextField
              label="Anotações"
              value={editingVideo?.note || ''}
              onChange={(e) => setEditingVideo(prev => ({ ...prev, note: e.target.value }))}
              fullWidth
              multiline
              rows={3}
              placeholder="Adicione suas anotações sobre este vídeo..."
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditVideoDialog(false)}>Cancelar</Button>
          <Button 
            onClick={handleEditVideo}
            variant="contained"
            sx={{
              bgcolor: customColors?.primary || 'primary.main',
              color: customColors?.primary ? getContrastText(customColors.primary) : 'white',
              '&:hover': {
                bgcolor: customColors?.primary || 'primary.main',
                opacity: 0.9
              }
            }}
          >
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      <PlanComparisonDialog 
        open={showPlanDialog} 
        onClose={() => setShowPlanDialog(false)}
        title="Upgrade para o Plano PRO"
        customColors={customColors}
      />
    </PageLayout>
  );
}

